/**
 * Language context providing i18n support for the Tourist Tools application.
 * Wraps the app tree and provides the active translation object plus language controls.
 * Language preference is persisted in localStorage under 'touristtools-lang'.
 */
import React, { createContext, useContext, useState } from 'react';
import { RU, pluralize, pluralizeEn, TranslationKeys } from './ru';
import { EN } from './en';

// ============================================
// Types
// ============================================

export type Language = 'ru' | 'en';

interface LanguageContextValue {
  t: TranslationKeys;
  language: Language;
  setLanguage: (lang: Language) => void;
  pluralizeWord: (count: number, forms: [string, string, string]) => string;
}

// ============================================
// Context
// ============================================

const LanguageContext = createContext<LanguageContextValue | null>(null);

// ============================================
// Provider
// ============================================

const STORAGE_KEY = 'touristtools-lang';

function getInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ru') {
      return stored;
    }
  } catch {
    // localStorage may not be available in some environments
  }
  return 'ru';
}

function getTranslation(lang: Language): TranslationKeys {
  return lang === 'en' ? EN : RU;
}

function getPluralizerForLanguage(lang: Language): (count: number, forms: [string, string, string]) => string {
  return lang === 'en' ? pluralizeEn : pluralize;
}

export function LanguageProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  function setLanguage(lang: Language): void {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage may not be available in some environments
    }
    setLanguageState(lang);
  }

  const value: LanguageContextValue = {
    t: getTranslation(language),
    language,
    setLanguage,
    pluralizeWord: getPluralizerForLanguage(language),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useTranslation(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within LanguageProvider');
  }
  return ctx;
}

// ============================================
// Re-export helpers so consumers can import everything from one place
// ============================================

export { formatMessage, formatDate } from './ru';
export type { TranslationKeys } from './ru';
